import startCase from 'lodash/startCase';
import type { V1Run } from '@/generated/hooks/jeeves/models';
import { runStateKeys } from './consts';

/**
 * Maps V1RunState field names to API filter values (PascalCase).
 * These values must match the keys in runStateKeys to ensure consistency.
 * The translations for these values are defined in automation.json under automation.runState.*
 */
// Ordered by display priority: when multiple state fields are set, the first
// match wins in mapRunStateToFilterValue/getActiveRunStateKey.
const RUN_STATE_TO_FILTER_VALUE = {
  completed: 'Completed',
  stopped: 'Stopped',
  awaiting_guidance: 'WaitingForGuidance',
  pending: 'Pending',
  executing: 'Executing',
} as const;

/** Run state keys that have a translated label and appear in the filter dropdown. */
const KNOWN_STATE_KEYS = Object.keys(RUN_STATE_TO_FILTER_VALUE) as Array<
  keyof typeof RUN_STATE_TO_FILTER_VALUE
>;

/** Fields on V1RunState that are metadata, not lifecycle indicators. */
const NON_STATE_FIELDS = new Set<string>(['update_time']);

type TRunState = NonNullable<V1Run['state']>;

/**
 * Maps V1RunState keys (camelCase/snake_case) to API filter values (PascalCase).
 *
 * @param state - The run state object from V1Run
 * @returns API filter value in PascalCase, or null if state isn't in the dropdown set
 */
export const mapRunStateToFilterValue = (
  state: V1Run['state'],
): string | null => {
  if (!state) return null;
  const key = KNOWN_STATE_KEYS.find(stateKey => state[stateKey]);
  return key ? RUN_STATE_TO_FILTER_VALUE[key] : null;
};

/**
 * Returns the first set lifecycle field on the run state. Used to surface
 * unhandled states (e.g. `stopping`, `failed`) instead of showing "-".
 *
 * @param state - The run state object from V1Run
 * @returns The active state key (e.g. "stopping", "completed") or null
 */
export const getActiveRunStateKey = (state: V1Run['state']): string | null => {
  if (!state) return null;
  const knownKey = KNOWN_STATE_KEYS.find(key => state[key]);
  if (knownKey) return knownKey;
  const otherKey = (Object.keys(state) as Array<keyof TRunState>).find(
    key => !NON_STATE_FIELDS.has(key) && state[key],
  );
  return otherKey ?? null;
};

/**
 * Returns display text and color for a run state. Known dropdown states use
 * their translated label; any other set state field is humanized via startCase
 * (so `stopping` → "Stopping"). Only truly empty/missing states render as "-".
 *
 * @param state - The run state object from V1Run
 * @returns Text + color className for the status cell
 */
export const getRunStateDisplay = (
  state: V1Run['state'],
): { text: string; color: string } => {
  const filterValue = mapRunStateToFilterValue(state);
  if (filterValue && runStateKeys[filterValue]) {
    let color = 'default';
    if (filterValue === RUN_STATE_TO_FILTER_VALUE.completed) {
      color = 'text-success';
    } else if (filterValue === RUN_STATE_TO_FILTER_VALUE.stopped) {
      color = 'text-destructive';
    } else if (
      filterValue === RUN_STATE_TO_FILTER_VALUE.pending ||
      filterValue === RUN_STATE_TO_FILTER_VALUE.awaiting_guidance
    ) {
      color = 'text-warning';
    } else if (filterValue === RUN_STATE_TO_FILTER_VALUE.executing) {
      color = 'text-muted-foreground';
    }
    return { text: runStateKeys[filterValue], color };
  }

  const activeKey = getActiveRunStateKey(state);
  if (activeKey) {
    return {
      text: startCase(activeKey),
      color: 'text-muted-foreground',
    };
  }

  return { text: '-', color: 'default' };
};

/**
 * Maps run state to a Badge variant. Unknown states fall back to "secondary"
 * so the humanized label still renders visibly instead of "-".
 *
 * @param state - The run state object from V1Run
 * @returns Badge variant string
 */
export const getRunStateBadgeVariant = (
  state: V1Run['state'],
): 'success' | 'destructive' | 'warning' | 'secondary' | 'outline' => {
  const filterValue = mapRunStateToFilterValue(state);
  if (filterValue) {
    if (filterValue === RUN_STATE_TO_FILTER_VALUE.completed) return 'success';
    if (filterValue === RUN_STATE_TO_FILTER_VALUE.stopped) return 'destructive';
    if (
      filterValue === RUN_STATE_TO_FILTER_VALUE.pending ||
      filterValue === RUN_STATE_TO_FILTER_VALUE.awaiting_guidance
    ) {
      return 'warning';
    }
    if (filterValue === RUN_STATE_TO_FILTER_VALUE.executing) return 'secondary';
  }

  if (getActiveRunStateKey(state)) return 'secondary';

  return 'outline';
};
